import { computed, type Component } from 'vue'
import { useRoute } from 'vue-router'
import HomeOutlineIcon from 'vue-material-design-icons/HomeOutline.vue'
import AccountGroupIcon from 'vue-material-design-icons/AccountGroup.vue'
import MessageTextOutlineIcon from 'vue-material-design-icons/MessageTextOutline.vue'
import BookOpenVariantIcon from 'vue-material-design-icons/BookOpenVariant.vue'
import CogOutlineIcon from 'vue-material-design-icons/CogOutline.vue'
import AccountOutlineIcon from 'vue-material-design-icons/AccountOutline.vue'
import CreationOutlineIcon from 'vue-material-design-icons/CreationOutline.vue'
import EarthIcon from 'vue-material-design-icons/Earth.vue'

export interface NavItem {
  label: string
  to: string
  name: string
  icon: Component
  section: string
}

export const primaryNavItems: NavItem[] = [
  { label: 'Home', to: '/', name: 'home', icon: HomeOutlineIcon, section: 'primary' },
  { label: 'Characters', to: '/characters', name: 'characters', icon: AccountGroupIcon, section: 'primary' },
  { label: 'Conversations', to: '/conversations', name: 'conversations', icon: MessageTextOutlineIcon, section: 'primary' },
  { label: 'Lorebooks', to: '/lorebooks', name: 'lorebooks', icon: BookOpenVariantIcon, section: 'primary' },
  { label: 'Worlds', to: '/worlds', name: 'worlds', icon: EarthIcon, section: 'primary' },
  { label: 'Open Worlds', to: '/open-worlds', name: 'open-worlds', icon: EarthIcon, section: 'primary' },
  { label: 'Personas', to: '/personas', name: 'personas', icon: AccountOutlineIcon, section: 'primary' },
  { label: 'Creator', to: '/creation', name: 'creator', icon: CreationOutlineIcon, section: 'primary' },
]

export const secondaryNavItems: NavItem[] = [
  { label: 'Settings', to: '/settings', name: 'settings', icon: CogOutlineIcon, section: 'secondary' },
]

export const navItems: NavItem[] = [...primaryNavItems, ...secondaryNavItems]

// Mobile tab bar: 4 primary icon-only tabs (center slot reserved for overflow button)
// Profile tab is added dynamically in BottomTabBar (needs auth userId)
export const mobileTabItems: NavItem[] = [
  primaryNavItems.find(i => i.name === 'home'),
  primaryNavItems.find(i => i.name === 'characters'),
  primaryNavItems.find(i => i.name === 'conversations'),
].filter((x): x is NavItem => Boolean(x))

// Mobile overflow: items accessible via the center Storyteller button
export const mobileOverflowItems: NavItem[] = [
  primaryNavItems.find(i => i.name === 'creator'),
  primaryNavItems.find(i => i.name === 'lorebooks'),
  primaryNavItems.find(i => i.name === 'worlds'),
  primaryNavItems.find(i => i.name === 'open-worlds'),
  primaryNavItems.find(i => i.name === 'personas'),
  secondaryNavItems.find(i => i.name === 'settings'),
].filter((x): x is NavItem => Boolean(x))

export function useNavigation() {
  const route = useRoute()

  const activeRoute = computed(() => route.name)

  const isActive = (item: NavItem): boolean => {
    if (item.to === '/' && route.name === 'home') return true
    if (item.to !== '/') {
      return route.path.startsWith(item.to)
    }
    return false
  }

  const isOverflowRouteActive = computed(() =>
    mobileOverflowItems.some(item => isActive(item))
  )

  return {
    navItems,
    primaryNavItems,
    secondaryNavItems,
    mobileTabItems,
    mobileOverflowItems,
    activeRoute,
    isActive,
    isOverflowRouteActive,
  }
}
